import { Eye, EyeOff, KeyRound, Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LICENSE_PRODUCTS,
  type GlobalLicenseListResult,
  type LicenseOperationResult,
  type LicenseProduct,
  type LicenseRevealResult,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import {
  Badge,
  Button,
  CopyButton,
  DescriptionList,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  Modal,
  Select,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { issueGlobalLicense, listGlobalLicenses, revealGlobalLicense, useQuery } from '@/data';
import { ApiError, errorMessage } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';

type NearMatch = Array<{ deviceId: string; masked?: string; distance?: number }>;

const newKey = () => crypto.randomUUID();

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function oneYearFromNow(): string {
  const out = new Date();
  out.setFullYear(out.getFullYear() + 1);
  return isoDay(out);
}

function ErrorBanner({ children }: { children: string }) {
  return (
    <p role="alert" className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
      {children}
    </p>
  );
}

function CodeBox({ id, label, value }: { id: string; label: string; value: string }) {
  return (
    <FormField label={label} htmlFor={id}>
      <textarea
        id={id}
        readOnly
        rows={4}
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full resize-none break-all rounded-md border border-line bg-surface-muted px-3 py-2 font-mono text-xs text-ink focus-visible:shadow-focus focus-visible:outline-none"
      />
    </FormField>
  );
}

export function LicenseEnginePage() {
  const canManage = useCan()('license.manage');
  const { data, error, loading, reload } = useQuery(() => listGlobalLicenses({ limit: 50 }), []);

  const [deviceId, setDeviceId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [product, setProduct] = useState<LicenseProduct>('Quds Pharmacy POS');
  const [expiryDate, setExpiryDate] = useState(oneYearFromNow());
  const [notes, setNotes] = useState('');
  const [nearMatch, setNearMatch] = useState<NearMatch | null>(null);
  const [acknowledgeNearMatch, setAcknowledgeNearMatch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [issued, setIssued] = useState<LicenseOperationResult | null>(null);
  const keyRef = useRef(newKey());

  const [revealTarget, setRevealTarget] = useState<GlobalLicenseListResult['licenses'][number] | null>(null);
  const [revealed, setRevealed] = useState<LicenseRevealResult | null>(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const revealKeyRef = useRef(newKey());
  const revealSeq = useRef(0);

  const resetIssueKey = () => {
    keyRef.current = newKey();
  };

  const submitIssue = async () => {
    if (submitting || !canManage) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const result = await issueGlobalLicense({
        product,
        deviceId: deviceId.trim(),
        customerName: customerName.trim(),
        expiryDate,
        licenseType: 'single_device',
        notes: notes.trim() || undefined,
        acknowledgeNearMatch,
        idempotencyKey: keyRef.current,
      });
      setIssued(result);
      void reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) {
        const d = e.details as { candidates?: NearMatch } | undefined;
        if (e.code === 'LICENSE_DEVICE_NEAR_MATCH' || d?.candidates) {
          setNearMatch(d?.candidates ?? []);
          setFormError('A similar Device ID already exists. Confirm the match below before issuing.');
          return;
        }
      }
      setFormError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const closeIssued = () => {
    setIssued(null);
    resetIssueKey();
  };

  const openReveal = (license: GlobalLicenseListResult['licenses'][number]) => {
    revealKeyRef.current = newKey();
    setRevealTarget(license);
    setRevealed(null);
    setRevealError(null);
    void loadReveal(license);
  };

  const loadReveal = useCallback(async (license: GlobalLicenseListResult['licenses'][number]) => {
    const seq = ++revealSeq.current;
    setRevealLoading(true);
    setRevealError(null);
    try {
      const res = await revealGlobalLicense(license.id, { idempotencyKey: revealKeyRef.current });
      if (seq !== revealSeq.current) return;
      if (res.licenseId !== license.id) throw new Error('Unexpected license returned — please retry.');
      setRevealed(res);
    } catch (e) {
      if (seq !== revealSeq.current) return;
      setRevealError(errorMessage(e, 'License could not be revealed. Please try again.'));
    } finally {
      if (seq === revealSeq.current) setRevealLoading(false);
    }
  }, []);

  const closeReveal = () => {
    revealSeq.current += 1;
    setRevealTarget(null);
    setRevealed(null);
    setRevealError(null);
    setRevealLoading(false);
  };

  useEffect(
    () => () => {
      setRevealed(null);
      setIssued(null);
    },
    [],
  );

  const issueValid = Boolean(deviceId.trim() && customerName.trim() && expiryDate && product);
  const licenses = data?.licenses ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="License Engine"
        subtitle="Issue and manage Production device licenses globally (not tied to a business workspace)."
        actions={
          <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={14} aria-hidden="true" />} onClick={() => void reload()}>
            Refresh list
          </Button>
        }
      />

      {canManage ? (
        <section className="rounded-lg border border-line bg-surface p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-ink">Issue license</h2>
          <p className="mt-0.5 text-xs text-ink-secondary">Generates a signed activation code for manual entry on the POS.</p>
          <form
            className="mt-4 grid gap-4 md:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (issueValid) void submitIssue();
            }}
          >
            {formError && (
              <div className="md:col-span-2">
                <ErrorBanner>{formError}</ErrorBanner>
              </div>
            )}
            <FormField label="Device ID" htmlFor="ge-device" required>
              <Input
                id="ge-device"
                value={deviceId}
                onChange={(e) => {
                  resetIssueKey();
                  setNearMatch(null);
                  setAcknowledgeNearMatch(false);
                  setDeviceId(e.target.value.toUpperCase());
                }}
                className="font-mono"
                autoComplete="off"
                spellCheck={false}
                disabled={submitting}
              />
            </FormField>
            <FormField label="Customer name" htmlFor="ge-customer" required>
              <Input
                id="ge-customer"
                value={customerName}
                onChange={(e) => {
                  resetIssueKey();
                  setCustomerName(e.target.value);
                }}
                disabled={submitting}
              />
            </FormField>
            <FormField label="Product" htmlFor="ge-product" required>
              <Select
                id="ge-product"
                value={product}
                onChange={(e) => {
                  resetIssueKey();
                  setProduct(e.target.value as LicenseProduct);
                }}
                options={LICENSE_PRODUCTS.map((p) => ({ value: p, label: p }))}
                disabled={submitting}
              />
            </FormField>
            <FormField label="Expiry" htmlFor="ge-expiry" required>
              <Input
                id="ge-expiry"
                type="date"
                value={expiryDate}
                onChange={(e) => {
                  resetIssueKey();
                  setExpiryDate(e.target.value);
                }}
                disabled={submitting}
              />
            </FormField>
            <FormField label="Notes (optional)" htmlFor="ge-notes" className="md:col-span-2">
              <Input
                id="ge-notes"
                value={notes}
                onChange={(e) => {
                  resetIssueKey();
                  setNotes(e.target.value);
                }}
                disabled={submitting}
              />
            </FormField>
            {nearMatch && nearMatch.length > 0 && (
              <div className="md:col-span-2 rounded-md border border-warning/30 bg-warning-soft px-3 py-3 text-sm">
                <p className="font-medium text-warning">Similar Device ID already exists</p>
                <ul className="mt-2 space-y-1 font-mono text-xs">
                  {nearMatch.map((c) => (
                    <li key={c.deviceId}>{c.deviceId}</li>
                  ))}
                </ul>
                <label className="mt-3 flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={acknowledgeNearMatch}
                    onChange={(e) => {
                      resetIssueKey();
                      setAcknowledgeNearMatch(e.target.checked);
                    }}
                  />
                  I confirm this is intentional and not a typo
                </label>
              </div>
            )}
            <div className="md:col-span-2 flex justify-end">
              <Button
                variant="primary"
                leftIcon={<Plus size={14} aria-hidden="true" />}
                loading={submitting}
                disabled={submitting || !issueValid || (Boolean(nearMatch?.length) && !acknowledgeNearMatch)}
                onClick={() => void submitIssue()}
              >
                Generate license
              </Button>
            </div>
          </form>
        </section>
      ) : (
        <p className="text-sm text-ink-secondary">View only — license.manage is required to issue licenses.</p>
      )}

      <section>
        <h2 className="mb-3 text-sm font-semibold text-ink">Recent licenses</h2>
        {loading && !data ? (
          <LoadingState label="Loading licenses…" />
        ) : error ? (
          <ErrorBanner>{errorMessage(error)}</ErrorBanner>
        ) : licenses.length === 0 ? (
          <EmptyState compact title="No licenses" description="Issued licenses from Production will appear here." />
        ) : (
          <TableContainer label="Global license history">
            <Table>
              <THead>
                <TR>
                  <TH>Device ID</TH>
                  <TH>Customer</TH>
                  <TH>Product</TH>
                  <TH>Expiry</TH>
                  <TH>Status</TH>
                  <TH>Issued</TH>
                  {canManage && <TH align="right">Actions</TH>}
                </TR>
              </THead>
              <TBody>
                {licenses.map((lic) => (
                  <TR key={lic.id}>
                    <TD>
                      <span className="font-mono text-xs">{lic.deviceId}</span>
                    </TD>
                    <TD>{lic.customerName}</TD>
                    <TD>{lic.product}</TD>
                    <TD>{formatDate(lic.expiryDate)}</TD>
                    <TD>
                      <Badge tone={lic.displayStatus === 'active' ? 'success' : 'warning'} dot>
                        {lic.displayStatus === 'active' ? 'Active' : 'Expired'}
                      </Badge>
                    </TD>
                    <TD>{formatDateTime(lic.generatedAt)}</TD>
                    {canManage && (
                      <TD align="right">
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<Eye size={14} aria-hidden="true" />}
                          onClick={() => openReveal(lic)}
                          aria-label={`Reveal license for ${lic.deviceId}`}
                        >
                          Reveal
                        </Button>
                      </TD>
                    )}
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        )}
        {data?.stale && <p className="mt-2 text-xs text-warning">Showing cached Production data — refresh when integration recovers.</p>}
      </section>

      {issued && (
        <Modal
          open
          onClose={closeIssued}
          title="License issued"
          icon={<KeyRound size={18} aria-hidden="true" />}
          size="md"
          footer={
            <>
              <CopyButton
                value={issued.activationCode}
                label="Copy license"
                copiedLabel="License copied"
                variant="secondary"
                size="md"
                toastTitle="License copied"
                className="mr-auto"
              />
              <Button variant="primary" onClick={closeIssued}>
                Done
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <DescriptionList
              items={[
                { label: 'Device', value: <span className="font-mono text-xs">{issued.payload.deviceId}</span> },
                { label: 'Expiry', value: formatDate(issued.payload.expiryDate) },
              ]}
            />
            <CodeBox id="ge-issued-code" label="Activation code" value={issued.activationCode} />
          </div>
        </Modal>
      )}

      {revealTarget && (
        <Modal
          open
          onClose={closeReveal}
          title="License activation code"
          description={revealTarget.deviceId}
          icon={<Eye size={18} aria-hidden="true" />}
          size="md"
          footer={
            <>
              {revealed && (
                <CopyButton
                  value={revealed.activationCode}
                  label="Copy license"
                  copiedLabel="License copied"
                  variant="secondary"
                  size="md"
                  toastTitle="License copied"
                  className="mr-auto"
                />
              )}
              <Button variant="primary" leftIcon={<EyeOff size={14} aria-hidden="true" />} onClick={closeReveal}>
                Hide
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {revealError && <ErrorBanner>{revealError}</ErrorBanner>}
            {revealLoading && <p className="text-sm text-ink-secondary">Revealing license…</p>}
            {!revealLoading && revealError && (
              <Button variant="secondary" size="sm" onClick={() => void loadReveal(revealTarget)}>
                Try again
              </Button>
            )}
            {revealed && (
              <>
                <DescriptionList
                  items={[
                    { label: 'Device', value: <span className="font-mono text-xs">{revealed.deviceId}</span> },
                    { label: 'Customer', value: revealed.customerName },
                    { label: 'Expiry', value: formatDate(revealed.expiryDate) },
                  ]}
                />
                <CodeBox id="ge-revealed-code" label="Activation code" value={revealed.activationCode} />
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
